import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { useCallback, useState } from "react";

import { CheckedInRoster } from "@/components/drop-in/checked-in-roster";
import { QueueWorkspace } from "@/components/queue/queue-workspace";
import { PartyMembershipRecovery } from "@/components/drop-in/party-membership-recovery";
import { useMyFacilityActions } from "@/hooks/use-my-facility-actions";

// DashboardTab owns the three local live-operation views. QueueWorkspace is the
// primary/default view and remains force-mounted while inactive so admission
// changes can refresh its canonical Queue-owned snapshot. The secondary roster
// and party-recovery views mount only when selected. QueueWorkspace renders in
// embedded mode so DropInServiceHome remains the only page-title owner.

export interface DashboardTabProps {
  facilityId: string;
  timeZone: string;
}

type LiveView = "queue" | "checked-in" | "parties";

export function DashboardTab({ facilityId, timeZone }: DashboardTabProps) {
  const { actions } = useMyFacilityActions(facilityId);
  const [activeView, setActiveView] = useState<LiveView>("queue");
  const [operationsRefreshGeneration, setOperationsRefreshGeneration] =
    useState(0);
  const canEndAdmission =
    actions.includes("facility:checkin") && actions.includes("facility:queue");
  const refreshOperations = useCallback(() => {
    setOperationsRefreshGeneration((generation) => generation + 1);
  }, []);

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={activeView}
          onChange={(_event, value: LiveView) => setActiveView(value)}
          aria-label="Live drop-in views"
          variant="fullWidth"
          sx={{
            width: "100%",
            maxWidth: 720,
            "& .MuiTab-root": {
              minWidth: 0,
              px: { xs: 0.5, sm: 2 },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              lineHeight: 1.25,
              whiteSpace: "normal",
            },
          }}
        >
          <Tab
            value="queue"
            label="Queue & courts"
            id="live-drop-in-tab-queue"
            aria-controls="live-drop-in-panel-queue"
          />
          <Tab
            value="checked-in"
            label="Checked-in players"
            id="live-drop-in-tab-checked-in"
            aria-controls="live-drop-in-panel-checked-in"
          />
          <Tab
            value="parties"
            label="Party issues"
            id="live-drop-in-tab-parties"
            aria-controls="live-drop-in-panel-parties"
          />
        </Tabs>
      </Box>
      <Box
        role="tabpanel"
        id="live-drop-in-panel-queue"
        aria-labelledby="live-drop-in-tab-queue"
        hidden={activeView !== "queue"}
        sx={{
          display: activeView === "queue" ? "block" : "none",
          mx: { xs: -2, lg: -3 },
        }}
      >
        <QueueWorkspace
          key={facilityId}
          embedded
          refreshGeneration={operationsRefreshGeneration}
        />
      </Box>
      {activeView === "checked-in" ? (
        <Box
          role="tabpanel"
          id="live-drop-in-panel-checked-in"
          aria-labelledby="live-drop-in-tab-checked-in"
        >
          <CheckedInRoster
            facilityId={facilityId}
            timeZone={timeZone}
            canEndAdmission={canEndAdmission}
            canCancelAdmission={canEndAdmission}
            onAdmissionStateChanged={refreshOperations}
          />
        </Box>
      ) : null}
      {activeView === "parties" ? (
        <Box
          role="tabpanel"
          id="live-drop-in-panel-parties"
          aria-labelledby="live-drop-in-tab-parties"
        >
          <PartyMembershipRecovery facilityId={facilityId} />
        </Box>
      ) : null}
    </Box>
  );
}
